
import React, { useState } from "react";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Grid,
  TextField,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  CircularProgress,
  Chip,
  Divider,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import EditIcon from "@mui/icons-material/Edit";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";

import { softColors } from "../../constants/colors";

const OrderCardMobile = ({
  order,
  orderIndex,
  handleQuantityChange,
  handleLineCheckboxChange,
  handleConfirmcheckedLines,
  handleDeltaCheckboxChange,
  fetchSerialNumbers,
  serialNumbers,
  updateOrderData,
  onToggleReviewed,
  isApproving,
}) => {
  const [openDialog, setOpenDialog] = useState(false);
  const [loadingSerials, setLoadingSerials] = useState(false);
  const [batchValues, setBatchValues] = useState({});
  const [noteValues, setNoteValues] = useState({});
  const [currentLine, setCurrentLine] = useState(null);
  const [currentLineIndex, setCurrentLineIndex] = useState(null);

  const handleBatchChange = (lineIndex, value) => {
    setBatchValues((prev) => ({ ...prev, [lineIndex]: value }));
    updateOrderData(orderIndex, lineIndex, { batch: value });
  };

  const handleNoteChange = (lineIndex, value) => {
    setNoteValues((prev) => ({ ...prev, [lineIndex]: value }));
    updateOrderData(orderIndex, lineIndex, { note: value });
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDialog = (line, lineIndex) => {
    setCurrentLine(line);
    setCurrentLineIndex(lineIndex);
    setOpenDialog(true);

    if (!serialNumbers[line.partname]) {
      setLoadingSerials(true);
      fetchSerialNumbers(line.partname).finally(() => setLoadingSerials(false));
    }
  };

  const handleSaveBatchAndNote = () => {
    if (currentLine && currentLineIndex !== null) {
      updateOrderData(orderIndex, currentLineIndex, {
        ...currentLine,
      });
    }
    handleCloseDialog();
  };

  return (
    <Box mb={3}>
      <Card elevation={2} sx={{ borderRadius: 2 }}>
        <CardContent sx={{ p: 0 }}>
          {/* Header Section */}
          <Box
            sx={{
              bgcolor: order.reviewed ? "#fff8e1" : "#ebf7ff",
              p: 2.5,
              display: "flex",
              flexDirection: "column",
              gap: 1,
              borderTopLeftRadius: 8,
              borderTopRightRadius: 8,
            }}
          >
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="h7">
                  הזמנה <strong>{order.ordname}</strong>
                </Typography>
                {order.reviewed && (
                  <Chip label="ממתין" size="small" color="warning" />
                )}
              </Box>
              <Chip label={order.ordstatusdes} size="small" />
            </Box>
            <Button
              variant="outlined"
              size="small"
              color={order.reviewed ? "warning" : "inherit"}
              startIcon={order.reviewed ? <VisibilityOffIcon /> : <VisibilityIcon />}
              onClick={() => onToggleReviewed(order)}
              sx={{
                alignSelf: "flex-start",
                borderColor: order.reviewed ? undefined : "#bbb",
                color: order.reviewed ? undefined : "#555",
              }}
            >
              {order.reviewed ? "בטל המתנה" : "סמן כממתין"}
            </Button>
          </Box>

          {/* Order Details Accordion */}
          <Accordion
            sx={{
              boxShadow: "none",
              "&:before": { display: "none" },
              backgroundColor: "transparent",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{
                px: 2.5,
                py: 1.5,
                "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
              }}
            >
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                  <Typography variant="body2">
                  <strong>{order.custname}</strong>  |{" "}
                    <Typography component="span">
                    {order.custdes}
                    </Typography>
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    תאריך אספקה: <strong>{order.neo_duedate}</strong>
                  </Typography>
                </Box>
              </Box>
            </AccordionSummary>

            <AccordionDetails
              sx={{ px: 2.5, py: 2, bgcolor: "rgba(0,0,0,0.02)" }}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                <Typography variant="body2" sx={{ mb: 0.5 }}>
                    מספר טלפון:{" "} <strong>{order.phone}</strong>
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    סניף: <strong>{order.branchname}</strong> | תת חברה:{" "}
                    <strong>{order.companyname}</strong>
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    סוג הזמנה:{" "}
                    <strong>
                      {order.typeDes} - {order.typeCode}
                    </strong>
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    לטיפול: <strong>{order.neo_ownerlogin}</strong>
                  </Typography>

                  <Typography variant="body2" sx={{ mb: 0.5 }}>
                    תאריך הזמנה: <strong>{order.curdate}</strong>
                  </Typography>
                  <Typography variant="body2">
                    לקוח מרכז:{" "}
                    {order.mcustname && order.mcustdes ? (
                      <strong>
                        {order.mcustname}
                        <br />
                        {order.mcustdes}
                      </strong>
                    ) : (
                      ""
                    )}
                  </Typography>
                  <Typography variant="body2">
                    לקוח מקבל:{" "}
                    {order.endcustname && order.endcustdes ? (
                      <strong>
                        {order.endcustname} <br /> {order.endcustdes}</strong>
                    ) : (
                      ""
                    )}
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          <Divider />

          {/* Order Lines Section */}
          <Box sx={{ p: 2.5 }}>
            <Typography variant="subtitle1" fontWeight={600} mb={2}>
              שורות הזמנה
            </Typography>

            {order.lines?.map((line, lineIndex) => (
              <Box
                key={`${orderIndex}-${lineIndex}`}
                mb={2}
                sx={{
                  bgcolor: line.isDuplicate
                    ? "#fff3cd"
                    : line.checked
                    ? "rgba(76, 175, 80, 0.08)"
                    : "background.paper",
                  borderRadius: 2,
                  border: "1px solid",
                  borderColor: line.isDuplicate 
                    ? "#ff9800" 
                    : line.checked 
                    ? softColors.success 
                    : softColors.border,
                  borderWidth: line.isDuplicate ? "2px" : "1px",
                  p: 2,
                  transition: "all 0.3s ease",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                  <Typography variant="subtitle2" fontWeight={600}>
                    {line.partdes}
                  </Typography>
                  {line.isDuplicate && (
                    <Chip
                      icon={<WarningAmberIcon />}
                      label="כפילות"
                      size="small"
                      color="warning"
                      sx={{ height: "22px", fontSize: "0.7rem" }}
                    />
                  )}
                </Box>
                <Typography variant="body2" color="text.secondary" mb={1}>
                  מק"ט: {line.partname}
                </Typography>

                <Box
                  sx={{
                    bgcolor: "background.paper",
                    p: 1.5,
                    borderRadius: 1,
                    mb: 2,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    מורשים לאשר:
                  </Typography>
                  <Typography variant="body2">
                    {line.neo_authoemail}
                    {line.neo_authoemail2 && (
                      <>
                        <br />
                        {line.neo_authoemail2}
                      </>
                    )}
                  </Typography>
                </Box>

                <Typography variant="body2" mb={2}>
                  מ. זמין למכירה: <strong>{line.balance}</strong> ({line.unitname4})
                </Typography>

                <Box
                  sx={{
                    bgcolor: "background.paper",
                    p: 1.5,
                    borderRadius: 1,
                    mb: 2,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <Typography variant="body2" fontWeight={600} mb={1}>
                    נתוני מכירות
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    ממוצע מכירות 6 חודשים:{" "}
                    <strong>
                      {line.salesData?.factoryQuantityLast6Avg?.toLocaleString('he-IL') || 0}
                    </strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    כמות מכירות חודש שעבר:{" "}
                    <strong>
                      {line.salesData?.factoryQuantityLast1?.toLocaleString('he-IL') || 0}
                    </strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    כמות מכירות חודש נוכחי:{" "}
                    <strong>
                      {line.salesData?.factoryQuantityCurrent?.toLocaleString('he-IL') || 0}
                    </strong>
                  </Typography>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <TextField
                      label={`כמות בהזמנה (${line.tunitname})`}
                      value={` ${line.quant} `}
                      variant="outlined"
                      size="small"
                      InputProps={{ readOnly: true }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <TextField
                      type="number"
                      label="כמות מאושרת"
                      value={
                        line.newQuant !== undefined ? line.newQuant : line.quant
                      }
                      onChange={(event) =>
                        handleQuantityChange(orderIndex, lineIndex, event)
                      }
                      variant="outlined"
                      size="small"
                      fullWidth
                      error={line.newQuant === 0}
                      helperText={
                        line.newQuant === 0
                          ? "שים לב הכמות 0 - השורה תסגר"
                          : " "
                      }
                    />
                  </Grid>
                </Grid>

                <Button
                  variant="outlined"
                  onClick={() => handleOpenDialog(line, lineIndex)}
                  fullWidth
                  sx={{
                    mt: 2,
                    borderColor: "divider",
                    color: "text.primary",
                    "&:hover": {
                      borderColor: "primary.main",
                      bgcolor: "rgba(0, 0, 0, 0.02)",
                    },
                  }}
                  startIcon={<EditIcon />}
                >
                  {line.note || line.batch
                    ? "ערוך פרטים נוספים"
                    : "פרטים נוספים"}
                </Button>

                <Box mt={2}>
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={6}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography variant="body2">הזמנת יתרה</Typography>
                        <IconButton
                          onClick={() =>
                            handleDeltaCheckboxChange(orderIndex, lineIndex)
                          }
                          disabled={
                            line.newQuant === undefined || line.newQuant === 0
                          }
                          size="small"
                          sx={{ ml: 1 }}
                        >
                          {line.deltaEnabled ? (
                            <TaskAltIcon color="success" />
                          ) : (
                            <RadioButtonUncheckedIcon />
                          )}
                        </IconButton>
                      </Box>
                    </Grid>
                    <Grid item xs={6}>
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <Typography variant="body2">מאושר</Typography>
                        <IconButton
                          onClick={() =>
                            handleLineCheckboxChange(orderIndex, lineIndex)
                          }
                          size="small"
                          sx={{ ml: 1 }}
                        >
                          {line.checked ? (
                            <TaskAltIcon color="success" />
                          ) : (
                            <RadioButtonUncheckedIcon />
                          )}
                        </IconButton>
                      </Box>
                    </Grid>
                  </Grid>
                </Box>
              </Box>
            ))}

            <Button
              variant="contained"
              onClick={() => handleConfirmcheckedLines(order)}
              fullWidth
              disabled={isApproving}
              startIcon={isApproving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                mt: 2,
                py: 1.5,
                bgcolor: "#0563B0",
                "&:hover": {
                  bgcolor: "#045291",
                },
              }}
            >
              {isApproving ? "מאשר..." : "אשר שורות הזמנה"}
            </Button>
          </Box>

          {/* Dialog */}
          <Dialog
            open={openDialog}
            onClose={handleCloseDialog}
            PaperProps={{
              sx: {
                width: "80%",
                maxWidth: "500px",
              },
            }}
          >
            <DialogTitle>פרטים נוספים</DialogTitle>
            <DialogContent>
              {loadingSerials ? (
                <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <Box sx={{ pt: 2 }}>
                  <Box mb={2}>
                    <Typography variant="subtitle2" gutterBottom>
                      אצווה
                    </Typography>
                    <Select
                      fullWidth
                      size="small"
                      value={batchValues[currentLineIndex] || ""}
                      onChange={(e) =>
                        handleBatchChange(currentLineIndex, e.target.value)
                      }
                    >
                      {currentLine &&
                        serialNumbers[currentLine.partname]?.map(
                          (serial, idx) => (
                            <MenuItem key={idx} value={serial.SERIALNAME}>
                              {serial.SERIALNAME}
                            </MenuItem>
                          )
                        )}
                    </Select>
                  </Box>

                  <Box>
                    <Typography variant="subtitle2" gutterBottom>
                      הערה
                    </Typography>
                    <TextField
                      multiline
                      rows={3}
                      fullWidth
                      size="small"
                      value={noteValues[currentLineIndex] || ""}
                      onChange={(e) =>
                        handleNoteChange(currentLineIndex, e.target.value)
                      }
                    />
                  </Box>
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button
                onClick={handleCloseDialog}
                variant="outlined"
                sx={{ mr: 1 }}
              >
                ביטול
              </Button>
              <Button
                onClick={handleSaveBatchAndNote}
                variant="contained"
                sx={{
                  bgcolor: "#0563B0",
                  "&:hover": {
                    bgcolor: "#045291",
                  },
                }}
              >
                שמור
              </Button>
            </DialogActions>
          </Dialog>
        </CardContent>
      </Card>
    </Box>
  );
};

export default OrderCardMobile;
